import { forkJoin as observableForkJoin, ReplaySubject, timer } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import { HttpViewsService } from "../http/view/http-views.service";
import { RequestManagerService } from "./request-manager.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import * as _ from "lodash";
import * as moment from "moment";
import { EventSubject } from "./EventSubject";
import { AuthenticationService } from "../http/authentication/authentication.service";
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "./request-manager.service";
import * as i3 from "../http/authentication/authentication.service";
export class ViewDataService extends HttpViewsService {
    constructor(httpClient, requestManager, authenticationService) {
        super(httpClient);
        this.httpClient = httpClient;
        this.requestManager = requestManager;
        this.authenticationService = authenticationService;
        this.allViews = new ReplaySubject(1);
        this.allViewsForStatusOverview = new ReplaySubject(1);
        this.detailOverviewSubject = new EventSubject({});
        this.compareMeasurements = (one, two) => one.lastMeasuredDateTime === two.lastMeasuredDateTime;
        this.authenticationService.getAuthenticatedUser()
            .pipe(distinctUntilChanged((obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2)))
            .subscribe((user) => user ? this.initialize() : this.clear());
    }
    initialize() {
        setTimeout(() => this.fetchStatus(), 200); // Prioritize monitor load. See bug 1667;
        //this.getAllForInit();
        if (environment.prefetchMeasurements) {
            this.initDetailOverviews();
        }
        this.refresh = this.refresh || setInterval(() => this.fetchStatus(), environment.pollingIntervalInSeconds * 5000);
    }
    clear() {
        clearInterval(this.refresh);
        this.refresh = null;
        this.allViews.next(null);
        this.allViewsForStatusOverview.next(null);
        this.detailOverviewSubject.next({});
    }
    getForDetailOverview(id, selectedDateRange, detailStatusOverview) {
        if (ViewDataService.isDefault(selectedDateRange)) {
            // The default range is cached. We don't need to send a new request.
            this.startRefreshInterval(id);
            return this.detailOverviewSubject
                .pipe(map(obj => obj[id]), filter(obj => !_.isNil(obj)), distinctUntilChanged(this.compareMeasurements));
        }
        else {
            // It's outside of the default range. We must send a new request.
            return super.getForDetailOverview(id, selectedDateRange);
        }
    }
    getAllForStatusOverview() {
        return this.allViewsForStatusOverview.pipe(filter(obj => !_.isNil(obj)), distinctUntilChanged((one, two) => JSON.stringify(one) === JSON.stringify(two)));
    }
    getOneForStatusOverview(id) {
        return this.allViewsForStatusOverview
            .pipe(filter(monitors => !_.isNil(monitors)), distinctUntilChanged((one, two) => JSON.stringify(one) === JSON.stringify(two)), map((obj) => obj.filter(obj => obj.id == id)[0]), filter(obj => !_.isNil(obj)));
    }
    add(view) {
        return super.add(view).pipe(tap(() => this.clear()), tap(() => this.fetchStatus()));
    }
    deleteView(id) {
        return super.deleteView(id).pipe(tap(() => this.fetchStatus()));
    }
    setChangedOrder(id, order) {
        return super.setChangedOrder(id, order);
    }
    fetchStatus() {
        this.requestManager.addToQueue({
            priority: 2,
            observable: super.getAllForStatusOverview(),
            subscriptionFunc: (monitors) => this.allViewsForStatusOverview.next(monitors)
        });
    }
    startRefreshInterval(id) {
        timer(0, environment.pollingIntervalInSeconds * 1000).pipe(
        // We automatically unsubscribe to the timer (and thus stop updating) when we unsubscribe the measurement update source.
        takeUntil(this.detailOverviewSubject.onUnsubscribe()))
            .subscribe(() => this.updateMeasurements(id));
    }
    updateMeasurements(id) {
        // Fetch the latest to update.
        observableForkJoin([
            this.getDetailOverviewForCache(),
            this.getOneForStatusOverview(id).pipe(take(1))
        ])
            .pipe(take(1))
            .subscribe(results => {
            super.getForDetailOverview(id, ViewDataService.defaultTime(), results[1])
                .subscribe((updated) => {
                results[0][id] = updated;
                this.detailOverviewSubject.next(results[0]);
            });
        });
    }
    getAllForInit() {
        this.requestManager.addToQueue({
            priority: 8,
            observable: super.getAll(),
            subscriptionFunc: (monitors) => this.allViews.next(monitors)
        });
    }
    initDetailOverviews() {
        this.getAllForStatusOverview().subscribe(views => views.forEach(view => this.initDetailOverview(view.id, view)));
    }
    initDetailOverview(viewId, view) {
        const subscriptionFunc = detailOverview => this.getDetailOverviewForCache().pipe(take(1)).subscribe(obj => {
            obj[viewId] = detailOverview;
            this.detailOverviewSubject.next(obj);
        });
        // Request for the detail overview
        const observable = super.getForDetailOverview(viewId, ViewDataService.defaultTime(), view);
        this.requestManager.addToQueue({ priority: 10, observable, subscriptionFunc });
    }
    static isDefault(selectedDateRange) {
        return selectedDateRange.start.day() === this.defaultTime().start.day() && selectedDateRange.end.day() === this.defaultTime().end.day();
    }
    getDetailOverviewForCache() {
        return this.detailOverviewSubject
            .pipe(take(1));
    }
}
ViewDataService.defaultTime = () => ({ start: moment().subtract(1, 'month'), end: moment() });
ViewDataService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ViewDataService_Factory() { return new ViewDataService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.RequestManagerService), i0.ɵɵinject(i3.AuthenticationService)); }, token: ViewDataService, providedIn: "root" });
